import {
	createContext,
	type HTMLAttributes,
	type PointerEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	type ReactNode,
	type Ref,
	useCallback,
	useContext,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { ModalPortal } from "../../layout/Modal";
import { clamp, cn } from "../../lib/utils";
import { ClickableIcon } from "../../primitives";
import { trackingAttrs } from "../../utils";

const DEFAULT_PANEL_WIDTH = 460;
const PANEL_MAX_HEIGHT_VH = 80;
const EDGE_PADDING = 16;
const MIN_PANEL_WIDTH = 320;
const MIN_PANEL_HEIGHT = 200;
const RESIZE_KEY_STEP = 16;
const RESIZE_KEY_DELTAS: Record<string, { width: number; height: number }> = {
	ArrowLeft: { width: -RESIZE_KEY_STEP, height: 0 },
	ArrowRight: { width: RESIZE_KEY_STEP, height: 0 },
	ArrowUp: { width: 0, height: -RESIZE_KEY_STEP },
	ArrowDown: { width: 0, height: RESIZE_KEY_STEP },
};
const PANEL_CLASS_NAME =
	"fixed z-[200000] flex flex-col overflow-hidden rounded-lg border border-bc-strong bg-surface-base shadow-floating-panel outline-none";

/** Marks a region inside the panel that owns its own scrolling, so wheel events can be routed to it. */
export const panelScrollRegionProps = { "data-panel-scroll": "" } as const;

type Position = { x: number; y: number };
type Size = { width: number; height: number };

type PanelContextValue = {
	titleId: string;
	isDragging: boolean;
	onClose: () => void;
	closeTrackingId?: string;
	closeTestId: string;
	onHeaderPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
	onHeaderPointerMove: (e: PointerEvent<HTMLDivElement>) => void;
	onHeaderPointerUp: (e: PointerEvent<HTMLDivElement>) => void;
};

const PanelContext = createContext<PanelContextValue | null>(null);

const usePanelContext = () => {
	const ctx = useContext(PanelContext);
	if (ctx == null) {
		throw new Error("FloatingPanel subcomponents must be used within FloatingPanel");
	}
	return ctx;
};

type Placement = "left" | "right";

/** A size for a panel whose top-left sits at `rect`, kept between the minimum and the viewport edge. */
function clampSize(rect: DOMRect, width: number, height: number): Size {
	return {
		width: clamp(width, MIN_PANEL_WIDTH, window.innerWidth - rect.left - EDGE_PADDING),
		height: clamp(height, MIN_PANEL_HEIGHT, window.innerHeight - rect.top - EDGE_PADDING),
	};
}

function defaultPosition(panelWidth: number, placement: Placement): Position {
	const width = Math.min(panelWidth, window.innerWidth - EDGE_PADDING * 2);
	return {
		x:
			placement === "left"
				? EDGE_PADDING * 2
				: Math.max(EDGE_PADDING, window.innerWidth - width - EDGE_PADDING * 2),
		y: Math.max(EDGE_PADDING, Math.round(window.innerHeight * 0.12)),
	};
}

function clampPosition(pos: Position, panelWidth: number, panelHeight: number): Position {
	const maxX = Math.max(EDGE_PADDING, window.innerWidth - panelWidth - EDGE_PADDING);
	const maxY = Math.max(EDGE_PADDING, window.innerHeight - panelHeight - EDGE_PADDING);
	return {
		x: clamp(pos.x, EDGE_PADDING, maxX),
		y: clamp(pos.y, EDGE_PADDING, maxY),
	};
}

type RootProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<HTMLDivElement>;
	children: ReactNode;
	onClose: () => void;
	width?: number;
	/** Which edge holds still when `width` changes, so growing or shrinking doesn't shift content. */
	resizeAnchor?: "left" | "right";
	/** Which side of the viewport the panel opens against, before any drag. */
	placement?: Placement;
	/** Adds a bottom-right handle for resizing. A dragged size wins over later `width` changes. */
	resizable?: boolean;
	trackingId?: string;
	testId?: string;
	closeTrackingId?: string;
	closeTestId?: string;
};

function Root({
	children,
	onClose,
	width = DEFAULT_PANEL_WIDTH,
	resizeAnchor = "left",
	placement = "right",
	resizable = false,
	className,
	trackingId,
	testId = "floating-panel",
	closeTrackingId,
	closeTestId = "floating-panel-close",
	ref,
	...rest
}: RootProps) {
	const titleId = useId();
	const panelRef = useRef<HTMLDivElement>(null);
	const measuredWidthRef = useRef<number | null>(null);
	const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
	const dragSizeRef = useRef<{ width: number; height: number } | null>(null);
	const [position, setPosition] = useState<Position>(() => defaultPosition(width, placement));
	const [isDragging, setIsDragging] = useState(false);
	const resizeStartRef = useRef<{ x: number; y: number; rect: DOMRect } | null>(null);
	const [resizedSize, setResizedSize] = useState<Size | null>(null);
	const [isResizing, setIsResizing] = useState(false);

	const setRefs = useCallback(
		(node: HTMLDivElement | null) => {
			panelRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) ref.current = node;
		},
		[ref],
	);

	useEffect(() => {
		panelRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [onClose]);

	// Measured rather than derived from `width`, which the viewport clamp in the style below can cap.
	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;
		const rect = panel.getBoundingClientRect();
		const previousWidth = measuredWidthRef.current;
		measuredWidthRef.current = rect.width;
		const shift =
			resizeAnchor === "right" && previousWidth != null ? previousWidth - rect.width : 0;
		setPosition((current) =>
			clampPosition({ x: current.x + shift, y: current.y }, rect.width, rect.height),
		);
	}, [width, resizeAnchor]);

	useEffect(() => {
		const onResize = () => {
			const panel = panelRef.current;
			if (!panel) return;
			const rect = panel.getBoundingClientRect();
			const width = Math.min(rect.width, window.innerWidth - EDGE_PADDING * 2);
			const height = Math.min(rect.height, window.innerHeight - EDGE_PADDING * 2);
			setResizedSize((current) => (current ? { width, height } : current));
			setPosition((current) => clampPosition(current, width, height));
		};
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// Stops native scroll chaining to the page. React registers onWheel passively, so this listener
	// has to be attached manually for preventDefault to take effect.
	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;
		const onWheel = (e: WheelEvent) => {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;
			// Plugin-rendered documents (PDF embeds) scroll their own content.
			if (target.closest("embed, iframe")) return;
			const region = target.closest<HTMLElement>("[data-panel-scroll]");
			if (!region || !panel.contains(region)) {
				e.preventDefault();
				return;
			}
			const atTop = region.scrollTop <= 0;
			const atBottom = Math.ceil(region.scrollTop + region.clientHeight) >= region.scrollHeight;
			if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) e.preventDefault();
		};
		panel.addEventListener("wheel", onWheel, { passive: false });
		return () => panel.removeEventListener("wheel", onWheel);
	}, []);

	const onHeaderPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
		if (e.button !== 0) return;
		const target = e.target instanceof Element ? e.target : null;
		if (target?.closest("[data-panel-no-drag]")) return;
		const panel = panelRef.current;
		if (!panel) return;
		const rect = panel.getBoundingClientRect();
		dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
		dragSizeRef.current = { width: rect.width, height: rect.height };
		setIsDragging(true);
		e.currentTarget.setPointerCapture(e.pointerId);
	}, []);

	const onHeaderPointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
		const offset = dragOffsetRef.current;
		const size = dragSizeRef.current;
		if (!offset || !size) return;
		setPosition(
			clampPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y }, size.width, size.height),
		);
	}, []);

	const onHeaderPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
		dragOffsetRef.current = null;
		dragSizeRef.current = null;
		setIsDragging(false);
		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			e.currentTarget.releasePointerCapture(e.pointerId);
		}
	}, []);

	const onResizePointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
		if (e.button !== 0) return;
		const panel = panelRef.current;
		if (!panel) return;
		e.preventDefault();
		resizeStartRef.current = { x: e.clientX, y: e.clientY, rect: panel.getBoundingClientRect() };
		setIsResizing(true);
		e.currentTarget.setPointerCapture(e.pointerId);
	}, []);

	const onResizePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
		const start = resizeStartRef.current;
		if (!start) return;
		setResizedSize(
			clampSize(
				start.rect,
				start.rect.width + e.clientX - start.x,
				start.rect.height + e.clientY - start.y,
			),
		);
	}, []);

	const onResizeKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
		const delta = RESIZE_KEY_DELTAS[e.key];
		const panel = panelRef.current;
		if (!delta || !panel) return;
		e.preventDefault();
		const rect = panel.getBoundingClientRect();
		setResizedSize(clampSize(rect, rect.width + delta.width, rect.height + delta.height));
	}, []);

	const onResizePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
		resizeStartRef.current = null;
		setIsResizing(false);
		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			e.currentTarget.releasePointerCapture(e.pointerId);
		}
	}, []);

	const contextValue = useMemo(
		() => ({
			titleId,
			isDragging,
			onClose,
			closeTrackingId,
			closeTestId,
			onHeaderPointerDown,
			onHeaderPointerMove,
			onHeaderPointerUp,
		}),
		[
			titleId,
			isDragging,
			onClose,
			closeTrackingId,
			closeTestId,
			onHeaderPointerDown,
			onHeaderPointerMove,
			onHeaderPointerUp,
		],
	);

	return (
		<PanelContext.Provider value={contextValue}>
			<ModalPortal>
				<div
					ref={setRefs}
					data-testid={testId}
					role="dialog"
					aria-modal="false"
					aria-labelledby={titleId}
					tabIndex={-1}
					className={cn(
						PANEL_CLASS_NAME,
						// Plugin-rendered documents swallow pointer events, which would stall a resize drag.
						isResizing &&
							"select-none [&_embed]:pointer-events-none [&_iframe]:pointer-events-none",
						className,
					)}
					style={{
						left: position.x,
						top: position.y,
						...(resizedSize
							? resizedSize
							: {
									width: `min(${width}px, calc(100vw - ${EDGE_PADDING * 2}px))`,
									maxHeight: `${PANEL_MAX_HEIGHT_VH}vh`,
								}),
					}}
					// Portaled content still bubbles through the React tree, where host layouts translate
					// wheel events over their chrome into programmatic page scrolling.
					onWheel={(e) => e.stopPropagation()}
					{...trackingAttrs(trackingId)}
					{...rest}
				>
					{children}
					{resizable && (
						<div
							role="button"
							tabIndex={0}
							aria-label="Resize panel. Use arrow keys to resize."
							data-testid={`${testId}-resize-handle`}
							className="absolute bottom-0 right-0 flex size-4 cursor-nwse-resize touch-none items-end justify-end rounded-br-lg p-1 focus-visible:outline-none focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30"
							onKeyDown={onResizeKeyDown}
							onPointerDown={onResizePointerDown}
							onPointerMove={onResizePointerMove}
							onPointerUp={onResizePointerUp}
							onPointerCancel={onResizePointerUp}
						>
							<div className="size-2 rounded-br-sm border-b-2 border-r-2 border-bc-strong" />
						</div>
					)}
				</div>
			</ModalPortal>
		</PanelContext.Provider>
	);
}
Root.displayName = "FloatingPanel";

type HeaderProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	trackingId?: string;
};

function Header({ className, trackingId, children, ref, ...rest }: HeaderProps) {
	const { isDragging, onHeaderPointerDown, onHeaderPointerMove, onHeaderPointerUp } =
		usePanelContext();
	return (
		<div
			ref={ref}
			className={cn(
				"flex shrink-0 cursor-grab select-none items-start gap-2 border-b border-b-bc-default bg-surface-neutral px-5 py-3.5",
				isDragging && "cursor-grabbing",
				className,
			)}
			onPointerDown={onHeaderPointerDown}
			onPointerMove={onHeaderPointerMove}
			onPointerUp={onHeaderPointerUp}
			onPointerCancel={onHeaderPointerUp}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{children}
		</div>
	);
}
Header.displayName = "FloatingPanel.Header";

type TitleProps = Omit<HTMLAttributes<HTMLDivElement>, "color"> & {
	ref?: Ref<HTMLDivElement>;
	trackingId?: string;
};

function Title({ className, trackingId, children, ref, ...rest }: TitleProps) {
	const { titleId } = usePanelContext();
	return (
		<div
			ref={ref}
			id={titleId}
			className={cn("text-[14px] font-medium leading-[20px] text-primary", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{children}
		</div>
	);
}
Title.displayName = "FloatingPanel.Title";

type SubtitleProps = Omit<HTMLAttributes<HTMLDivElement>, "color"> & {
	ref?: Ref<HTMLDivElement>;
	trackingId?: string;
};

function Subtitle({ className, trackingId, children, ref, ...rest }: SubtitleProps) {
	return (
		<div
			ref={ref}
			className={cn("mt-1 text-[12px] font-normal leading-[18px] text-tertiary", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{children}
		</div>
	);
}
Subtitle.displayName = "FloatingPanel.Subtitle";

type ActionsProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	trackingId?: string;
};

function Actions({ className, trackingId, children, ref, ...rest }: ActionsProps) {
	const { onClose, closeTrackingId, closeTestId } = usePanelContext();
	return (
		<div
			ref={ref}
			data-panel-no-drag
			className={cn("flex shrink-0 items-center gap-2 pt-0.5", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{children}
			<ClickableIcon
				type="x-mark"
				size="small"
				color="secondary"
				testId={closeTestId}
				trackingId={closeTrackingId}
				aria-label="Close"
				onClick={(e) => {
					e.stopPropagation();
					onClose();
				}}
			/>
		</div>
	);
}
Actions.displayName = "FloatingPanel.Actions";

type BodyProps = HTMLAttributes<HTMLDivElement> & {
	ref?: Ref<HTMLDivElement>;
	trackingId?: string;
};

function Body({ className, trackingId, ref, ...rest }: BodyProps) {
	return (
		<div
			ref={ref}
			className={cn("flex min-h-0 flex-1 overflow-hidden", className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
}
Body.displayName = "FloatingPanel.Body";

type FooterProps = HTMLAttributes<HTMLElement> & {
	ref?: Ref<HTMLElement>;
	trackingId?: string;
};

function Footer({ className, trackingId, ref, ...rest }: FooterProps) {
	return (
		<footer
			ref={ref}
			className={cn(
				"flex shrink-0 items-center justify-between gap-4 border-t border-bc-default bg-surface-neutral px-5 py-3",
				className,
			)}
			{...trackingAttrs(trackingId)}
			{...rest}
		/>
	);
}
Footer.displayName = "FloatingPanel.Footer";

export const FloatingPanel = Object.assign(Root, {
	Header,
	Title,
	Subtitle,
	Actions,
	Body,
	Footer,
});

export type FloatingPanelProps = RootProps;
export type FloatingPanelHeaderProps = HeaderProps;
export type FloatingPanelBodyProps = BodyProps;
export type FloatingPanelFooterProps = FooterProps;
